import { SurvivorGameStatus } from '../types'

// Structural, so the rule stays a pure function: it needs a number and a status per round,
// nothing else the API happens to send with them.
type Round = { number: number; status: string }

type Game = {
  status: SurvivorGameStatus
  /** Last round the game played, across every member. Absent before anyone has picked. */
  lastPlayedGameweekNumber?: number | undefined
}

export type RoundBrowsing = {
  /** Round the page opens on, and the only one a pick can be submitted for. */
  currentGwNumber: number | undefined
  /** Furthest round the navigator may walk forward to. */
  maxGwNumber: number | undefined
}

/**
 * Which round the game page lands on, and how far forward it may be browsed.
 *
 * The two differ only on a finished game, and that difference is the whole point. A finished
 * game's rounds are bounded by the game itself — the competition may well have carried on past
 * it — so both values are the game's last played round.
 *
 * That last round is the GAME's, not the caller's. Deriving it from the caller's own picks (the
 * only source available before the server sent `lastPlayedGameweekNumber`) reads short for
 * anyone eliminated early: a player knocked out in round 24 of a game that ran to 27 got a
 * ceiling of 24 and could never step forward to see how it ended. Their picks stay as the
 * fallback for a game whose detail predates the field, where they are at least a lower bound.
 *
 * While a game is still running the ceiling is the competition's current round: an eliminated
 * player follows the survivors round by round, and there is nothing beyond it to show anyone.
 */
export function resolveRoundBrowsing(
  gameweeks: readonly Round[],
  game: Game,
  myPicks: readonly { gameweekNumber: number }[],
): RoundBrowsing {
  if (gameweeks.length === 0) return { currentGwNumber: undefined, maxGwNumber: undefined }

  const lastPlayed = lastPlayedRound(gameweeks, game, myPicks)

  if (game.status === SurvivorGameStatus.Finished && lastPlayed !== undefined) {
    return { currentGwNumber: lastPlayed, maxGwNumber: lastPlayed }
  }

  // Latest round that is under way or done. Falls back to the first round of a competition
  // that has not kicked off yet, so a game in registration still has something to show.
  const inFlight = [...gameweeks]
    .sort((a, b) => a.number - b.number)
    .reverse()
    .find((gw) => gw.status !== 'UPCOMING')
  const current = inFlight?.number ?? gameweeks[0]?.number

  return { currentGwNumber: current, maxGwNumber: current }
}

/** The game's last played round, as a round the competition actually has. */
function lastPlayedRound(
  gameweeks: readonly Round[],
  game: Game,
  myPicks: readonly { gameweekNumber: number }[],
): number | undefined {
  const number = game.lastPlayedGameweekNumber
    ?? (myPicks.length > 0 ? Math.max(...myPicks.map((p) => p.gameweekNumber)) : undefined)

  return number !== undefined && gameweeks.some((gw) => gw.number === number)
    ? number
    : undefined
}
